// Global file upload configuration
export const FILE_UPLOAD_CONFIG = {
  // Maximum file size in bytes (1MB = 1024 * 1024 bytes)
  MAX_FILE_SIZE: 1024 * 1024, // 1MB
  
  // Allowed file types with their MIME types and extensions
  ALLOWED_FILE_TYPES: {
    // Excel files
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    
    // Word documents
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    
    // PowerPoint files
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    
    // PDF files
    'application/pdf': ['.pdf'],
    
    // Image files
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png']
  },
  
  // Helper function to get all allowed extensions
  getAllowedExtensions(): string[] {
    const extensions: string[] = [];
    Object.values(this.ALLOWED_FILE_TYPES).forEach(exts => {
      extensions.push(...exts);
    });
    return extensions;
  },
  
  // Helper function to check if file type is allowed
  isFileTypeAllowed(file: File): boolean {
    // Check MIME type first
    if (this.ALLOWED_FILE_TYPES[file.type as keyof typeof this.ALLOWED_FILE_TYPES]) {
      return true;
    }
    
    // Fallback: check file extension
    const fileName = file.name.toLowerCase();
    const allowedExtensions = this.getAllowedExtensions();
    return allowedExtensions.some(ext => fileName.endsWith(ext));
  },
  
  // Helper function to validate file
  validateFile(file: File): { isValid: boolean; error?: string } {
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: `File size must be less than ${this.formatFileSize(this.MAX_FILE_SIZE)}`
      };
    }
    
    // Check file type
    if (!this.isFileTypeAllowed(file)) {
      const allowedExtensions = this.getAllowedExtensions().join(', ');
      return {
        isValid: false,
        error: `Only ${allowedExtensions} files are allowed`
      };
    }
    
    return { isValid: true };
  },
  
  // Helper function to format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },
  
  // Get human-readable allowed file types for display
  getAllowedFileTypesDisplay(): string {
    const extensions = this.getAllowedExtensions();
    return extensions.join(', ');
  }
};

// Standalone helper functions that don't rely on 'this' context
export const getAllowedExtensions = (): string[] => {
  const extensions: string[] = [];
  Object.values(FILE_UPLOAD_CONFIG.ALLOWED_FILE_TYPES).forEach(exts => {
    extensions.push(...exts);
  });
  return extensions;
};

export const isFileTypeAllowed = (file: File): boolean => {
  // Check MIME type first
  if (FILE_UPLOAD_CONFIG.ALLOWED_FILE_TYPES[file.type as keyof typeof FILE_UPLOAD_CONFIG.ALLOWED_FILE_TYPES]) {
    return true;
  }
  
  // Fallback: check file extension
  const fileName = file.name.toLowerCase();
  const allowedExtensions = getAllowedExtensions();
  return allowedExtensions.some(ext => fileName.endsWith(ext));
};

export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file size
  if (file.size > FILE_UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size must be less than ${formatFileSize(FILE_UPLOAD_CONFIG.MAX_FILE_SIZE)}`
    };
  }
  
  // Check file type
  if (!isFileTypeAllowed(file)) {
    const allowedExtensions = getAllowedExtensions().join(', ');
    return {
      isValid: false,
      error: `Only ${allowedExtensions} files are allowed`
    };
  }
  
  return { isValid: true };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getAllowedFileTypesDisplay = (): string => {
  const extensions = getAllowedExtensions();
  return extensions.join(', ');
};

// Export individual constants for easy access
export const MAX_FILE_SIZE = FILE_UPLOAD_CONFIG.MAX_FILE_SIZE;
export const ALLOWED_FILE_TYPES = FILE_UPLOAD_CONFIG.ALLOWED_FILE_TYPES; 